/**
 * PATH: packages/email-hub/src/feature/email-layout/useEmailSyncRuntime.ts
 * FILE_ID: EMAIL-LAYOUT-SYNC-RUNTIME-001
 * PURPOSE: Preserve EmailLayout's sync cadence, pauses, and backfill lifecycle.
 */

import { useEffect, useRef, useState } from 'react'
import type { useApiAuth } from '../../_internal/components/ApiAuthGate'
import { useCapacityPause } from '../../_internal/hooks/useCapacityPause'
import type { useToast } from '../../_internal/components/Toast'
import {
  EMAIL_SYNC_AUTH_FAILED_EVENT,
  type useEmailSyncStatus,
  type useOutlookStatus,
  type useSyncEmails,
} from '../hooks/useEmails'
import { useEmailBackfill } from '../hooks/useEmailBackfill'
import { setEmailTenantId } from '../lib/api'
import { rehydrateHiddenMailboxes } from '../stores/emailFilterStore'
import { useEmailSyncAction } from './useEmailSyncAction'

const AUTO_SYNC_STALE_MS = 5 * 60 * 1000
// The mount sync is delayed so the inbox's cold list fetch paints first.
const INITIAL_AUTO_SYNC_DELAY_MS = 3000

type Session = ReturnType<typeof useApiAuth>['session']
type Sync = ReturnType<typeof useSyncEmails>['sync']
type Outlook = ReturnType<typeof useOutlookStatus>
type SyncStatus = ReturnType<typeof useEmailSyncStatus>['data']
type Toast = ReturnType<typeof useToast>['toast']

interface UseEmailSyncRuntimeArgs {
  session: Session
  sync: Sync
  isSyncing: boolean
  syncStatus: SyncStatus
  outlook: Outlook
  outlookConnected: Outlook['connected']
  outlookExpired: Outlook['isExpired']
  toast: Toast
}

export function useEmailSyncRuntime({
  session,
  sync,
  isSyncing,
  syncStatus,
  outlook,
  outlookConnected,
  outlookExpired,
  toast,
}: UseEmailSyncRuntimeArgs) {
  const [lastSyncAt, setLastSyncAt] = useState<number | null>(null)
  const [, setTickForRelativeTime] = useState(0)
  useEffect(() => {
    if (!lastSyncAt) return
    const id = window.setInterval(
      () => setTickForRelativeTime(tick => tick + 1),
      30_000,
    )
    return () => window.clearInterval(id)
  }, [lastSyncAt])

  useEffect(() => {
    setEmailTenantId(session?.organisation?.id ?? null)
    rehydrateHiddenMailboxes(session?.organisation?.id ?? null)
  }, [session?.organisation?.id])

  const capacityPause = useCapacityPause()
  const { handleSync, capacityPauseRef } = useEmailSyncAction({
    sync,
    toast,
    outlook,
    capacityPause,
    setLastSyncAt,
  })

  const outlookRef = useRef(outlook)
  outlookRef.current = outlook
  const lastSyncAtRef = useRef<number | null>(null)
  lastSyncAtRef.current = lastSyncAt
  const isSyncingRef = useRef(false)
  isSyncingRef.current = isSyncing
  const neverSyncedRef = useRef(false)
  neverSyncedRef.current = Boolean(
    syncStatus
      && (syncStatus.summary?.active_mailbox_count ?? 0) > 0
      && syncStatus.summary?.oldest_sync_age_seconds == null
      && !syncStatus.active_sync,
  )

  const [authPaused, setAuthPaused] = useState(false)
  const authPausedRef = useRef(false)
  authPausedRef.current = authPaused
  useEffect(() => {
    const onAuthFail = (event: Event) => {
      setAuthPaused(true)
      const detail = (event as CustomEvent).detail as
        | { message?: string }
        | undefined
      const message = detail?.message
        ? `Sync paused: ${detail.message}. Reconnect Outlook or refresh to resume.`
        : 'Sync paused - your session is no longer valid. Reconnect Outlook or refresh.'
      toast(message, 'warning')
    }
    window.addEventListener(
      EMAIL_SYNC_AUTH_FAILED_EVENT,
      onAuthFail as EventListener,
    )
    return () => window.removeEventListener(
      EMAIL_SYNC_AUTH_FAILED_EVENT,
      onAuthFail as EventListener,
    )
  }, [toast])

  useEffect(() => {
    if (authPaused && outlookConnected === true && !outlookExpired) {
      setAuthPaused(false)
    }
  }, [authPaused, outlookConnected, outlookExpired])

  useEffect(() => {
    const tryAutoSync = () => {
      if (document.hidden) return
      if (typeof navigator !== 'undefined' && navigator.onLine === false) return
      if (isSyncingRef.current) return
      if (authPausedRef.current) return
      if (capacityPauseRef.current.isPausedRef.current) return
      const currentOutlook = outlookRef.current
      if (currentOutlook.connected !== true || currentOutlook.isExpired) return
      const last = lastSyncAtRef.current
      if (last && Date.now() - last < AUTO_SYNC_STALE_MS) return
      if (neverSyncedRef.current) {
        void handleSync({ silent: true, forceFull: true, daysBack: 90 })
      } else {
        void handleSync({ silent: true })
      }
    }
    const initialId = window.setTimeout(tryAutoSync, INITIAL_AUTO_SYNC_DELAY_MS)
    const id = window.setInterval(tryAutoSync, AUTO_SYNC_STALE_MS)
    let lastHiddenAt = 0
    const onVis = () => {
      if (document.hidden) {
        lastHiddenAt = Date.now()
      } else {
        tryAutoSync()
      }
    }
    const onFocus = () => {
      if (lastHiddenAt > 0 && Date.now() - lastHiddenAt < 30_000) {
        tryAutoSync()
      }
    }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onFocus)
    return () => {
      window.clearTimeout(initialId)
      window.clearInterval(id)
      document.removeEventListener('visibilitychange', onVis)
      window.removeEventListener('focus', onFocus)
    }
  }, [capacityPauseRef, handleSync])

  const backfill = useEmailBackfill({
    organisationId: session?.organisation?.id ?? null,
    userId: session?.user?.id ?? null,
    enabled:
      outlookConnected === true
      && !outlookExpired
      && !authPaused
      && !capacityPause.isPaused,
    sync,
    isSyncing,
    onChunkCompleted: () => setLastSyncAt(Date.now()),
  })

  return {
    lastSyncAt,
    handleSync,
    authPaused,
    setAuthPaused,
    backfill,
  }
}
